"use client";

import { type InputHTMLAttributes, type ReactNode, useState } from "react";
import { mergeClasses } from "@huub-web/utils";

import { Flex } from "../../Flex";
import { Card } from "../../Card";
import { Text } from "../../Text";
import { Reveal } from "../../Reveal";
import { Badge } from "../../Badge";
import { Icon } from "../../Icon";

import {
  cardContainerStyle,
  cardInputStyle,
  leadingStyle,
  labelStyle,
  radioCardStyle,
  cardInnerStyle,
} from "../RadioCards.css";

export type RadioCardOptionData = {
  value: string;
  label: string;
  leading?: ReactNode;
  disabled?: boolean;
};

export type RadioCardOptionProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value" | "type"
> & {
  /**
   * The option data
   */
  option: RadioCardOptionData;
  /**
   * Whether this option is checked
   */
  checked: boolean;
  /**
   * Whether the option is disabled
   */
  disabled?: boolean;
  /**
   * Whether to show error state
   */
  error?: boolean;
  /**
   * Whether to show highlight state
   */
  highlight?: boolean;
  /**
   * Whether to show success state
   */
  success?: boolean;
  /**
   * The radio group name
   */
  name: string;
  /**
   * The unique ID for this option
   */
  optionID: string;
  /**
   * Handler for value change
   */
  onChange: (value: string) => void;
  /**
   * Optional ref for the first input
   */
  inputRef?: React.RefObject<HTMLInputElement | null>;
};

/**
 * Individual RadioCardOption component with its own hover state.
 */
export const RadioCardOption = ({
  option,
  checked,
  disabled: disabledProp,
  error,
  success,
  highlight,
  name,
  optionID,
  onChange,
  inputRef,
  className,
  ...props
}: RadioCardOptionProps) => {
  const [hasHover, setHasHover] = useState(false);
  const isDisabled = disabledProp || option.disabled;

  const handleChange = () => {
    if (!isDisabled) {
      onChange(option.value);
    }
  };

  return (
    <Card
      key={option.value}
      as="label"
      htmlFor={optionID}
      className={mergeClasses(
        radioCardStyle,
        cardContainerStyle({
          checked,
          disabled: isDisabled,
        }),
        className,
      )}
      innerClassName={mergeClasses(
        cardInnerStyle({
          error,
          success,
          highlight,
        }),
      )}
      direction="vertical"
      variant={checked ? "tertiary" : "primary"}
      border={checked ? "transparent" : "default"}
      disabled={isDisabled}
      onClick={() => {}}
      onHoverChange={setHasHover}
      leading={
        checked &&
        success && (
          <Badge
            variant="success"
            offset="medium"
            size="s"
            value={<Icon icon="checkThick" size="xs" />}
          />
        )
      }
    >
      <input
        ref={inputRef}
        id={optionID}
        type="radio"
        name={name}
        value={option.value}
        checked={checked}
        className={cardInputStyle}
        onChange={handleChange}
        disabled={isDisabled}
        {...props}
      />

      <Flex direction="column" align="center" gap="s">
        {option.leading && (
          <div
            className={leadingStyle({
              checked,
              hover: hasHover,
            })}
            {...(isDisabled && { "data-disabled": true })}
          >
            {option.leading}
          </div>
        )}

        <Text align="center" className={labelStyle}>
          {option.label}
        </Text>

        {checked && success && <Reveal></Reveal>}
      </Flex>
    </Card>
  );
};

RadioCardOption.displayName = "RadioCardOption";
